import {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import _ from 'lodash';
import Grid from '@mui/material/Grid';
import moment from 'moment/moment';
import config from '../../../app/config';
import Typography from '@mui/material/Typography';
import {isChild} from '../admitPatient';
import AgyleTextField from '../../../components/ui/fields/agyleTextField';
import {useSummaryStyles} from '../admitStyles';
import AgyleAccordion from '../../../components/ui/accordion/agyleAccordion';
import FormLabel from '@mui/material/FormLabel';

const Summary = (props) => {
  const classes = useSummaryStyles();
  const { t } = useTranslation();
  const {
    admit,
    admission,
    bedBooking,
    bedBookingForm,
    isBedBookingChosen,
    isAddOnlyPatient,
    isSmall,
  } = props;

  const [summaryAccordionExpanded, setSummaryAccordionExpanded] =
    useState(true);

  const patientAge = moment().diff(admit.dob, "years");

  // SECTIONS
  const demographicsSection = [
    {
      label: "First Name",
      value: admit.firstName,
      key: "firstName",
      type: "text",
    },
    {
      label: "Middle Name",
      value: admit.middleNames,
      key: "middleNames",
      type: "text",
    },
    {
      label: "Family Name",
      value: admit.familyName,
      key: "familyName",
      type: "text",
    },
    {
      label: "Gender",
      value: admit.gender?.__description,
      key: "gender",
      type: "text",
    },
    {
      label: "Hospital Number",
      value: admit.hospitalNumber,
      key: "hospitalNumber",
      type: "text",
    },
    {
      label: "NHS Number",
      value: admit.nhsNumber,
      key: "nhsNumber",
      type: "text",
    },
    {
      label: "Date of Birth",
      value: admit.dob?.format(config?.defaultDateFormat),
      key: "dateOfBirth",
      type: "text",
    },
    { label: "Age", value: patientAge, key: "age", type: "text" },
    {
      label: "Overseas Status",
      value: admit.overseasStatus?.__description,
      key: "overseasStatus",
      type: "text",
    },
    {
      label: "Ethnic Group",
      value: admit.ethnicGroup?.__description,
      key: "ethnicGroup",
      type: "text",
    },
    {
      label: "Ehic Number",
      value: admit.ehicCard ? admit.ehicNumber : "",
      key: "ehicNumber",
      type: "text",
    },
    {
      label: "Reason for not providing EHIC Number",
      value: !admit.ehicCard ? admit.noEhicReason : "",
      key: "noEhicReason",
      type: "text",
    },
    {
      label: "Occupation",
      value: admit.occupation,
      key: "occupation",
      type: "text",
    },
    {
      label: "Email Address",
      value: admit.emailAdresses,
      key: "emailAddresses",
      type: "array",
    },
    {
      label: "Phone number",
      value: admit.phoneNumbers,
      key: "phoneNumbers",
      type: "array",
    },
    {
      label: "Employer Name",
      value: admit.employerName,
      key: "employerName",
      type: "text",
    },
    {
      label: "Employer Address",
      value: admit.employerAddress,
      key: "employerAddress",
      type: "text",
    },
    {
      label: "Employer Postal Code",
      value: admit.employerPostalCode,
      key: "employerPostalCode",
      type: "text",
    },
  ];

  const demographicsAddressSection = [
    { label: "Street", value: admit.street, key: "street", type: "text" },
    { label: "Building", value: admit.building, key: "building", type: "text" },
    { label: "City", value: admit.city, key: "city", type: "text" },
    {
      label: "Post Town",
      value: admit.postTown,
      key: "postTown",
      type: "text",
    },
    { label: "County", value: admit.county, key: "county", type: "text" },
    {
      label: "Postal Code",
      value: admit.postalCode,
      key: "postalCode",
      type: "text",
    },
  ];

  const demographicsTemporaryAddressSection = [
    {label: 'Street', value: admit.streetTemporary, key: 'streetTemporary', type: 'text'},
    {label: 'Post Town', value: admit.postTownTemporary, key: 'postTownTemporary', type: 'text'},
    {label: 'City', value: admit.cityTemporary, key: 'cityTemporary', type: 'text'},
    {
      label: 'County',
      value: admit.countyTemporary,
      key: 'countyTemporary',
      type: 'text',
    },
    {label: 'Postal Code', value: admit.postalCodeTemporary, key: 'postalCodeTemporary', type: 'text'},
  ];

  const nextOfKinSection = {
    label: "Next of Kin",
    value: admit.nokList,
    key: "nokList",
    type: "array",
  };

  const childSection = [
    {
      label: "School Name",
      value: admit.schoolName,
      key: "schoolName",
      type: "text",
    },
    {
      label: "School Contact",
      value: admit.schoolContact,
      key: "schoolContact",
      type: "text",
    },
    {
      label: "School Address",
      value: admit.schoolAddress,
      key: "schoolAddress",
      type: "text",
    },
    {
      label: "School Postal Code",
      value: admit.schoolPostalCode,
      key: "schoolPostalCode",
      type: "text",
    },
    { label: "CP-IS", value: admission.cpIs, key: "cpIs", type: "boolean" },
    {
      label: "CP-IS Alert Present",
      value: admission.cpIsAlertPresent,
      key: "cpIsAlertPresent",
      type: "boolean",
    },
  ];

  const attendanceSection = [
    {
      label: "Chief Complaint",
      value: admission.chiefComplaint?.__description,
      key: "chiefComplaint",
      type: "text",
    },
    {
      label: "Arrival Mode",
      value: admission.modeOfArrival?.snomedDescription,
      key: "modeOfArrival",
      type: "text",
    },
    {
      label: "Attendance Source",
      value: admission.attendanceSource?.__description,
      key: "attendanceSource",
      type: "text",
    },
    {
      label: "Location",
      value: admission.location?.__description,
      key: "location",
      type: "text",
    },
    {
      label: "Attendance Type",
      value: admission.attendanceType?.__description,
      key: "attendanceType",
      type: "text",
    },
    {
      label: "Emergency Care Attendance Category",
      value: admission.attendanceCategory?.__description,
      key: "attendanceCategory",
      type: "text",
    },
    {
      label: "Ambulance Incident Number",
      value: admission.ambulanceIncidentNumber,
      key: "ambulanceIncidentNumber",
      type: "text",
    },
    {
      label: "Care Contact Identifier",
      value: admission.careContactIdentifier,
      key: "careContactIdentifier",
      type: "text",
    },
    {
      label: "Org Id - Conveying Ambulance Trust",
      value: admission.conveyingAmbulanceOrgId,
      key: "conveyingAmbulanceOrgId",
      type: "text",
    },
    {
      label: "History of Presenting Complaint",
      value: admission.historyOfPresentingComplaint,
      key: "historyOfPresentingComplaint",
      type: "text",
    },
    {
      label: `${config.gpExpected ? "GP Expected" : "111"} Flag`,
      value: admission.flag111,
      key: "flag111",
      type: "boolean",
    },
    {
      label: `${config.gpExpected ? "GP Expected" : "111"} Flag Date`,
      value: admission.flag111Timestamp,
      key: "flag111Timestamp",
      type: "date",
    },
    {
      label: "Major Trauma",
      value: admission.majorTraumaFlag,
      key: "majorTraumaFlag",
      type: "boolean",
    },
  ];

  const getBedBookingAlarms = () => {
    let alarms;
    if (bedBooking) {
      if (bedBooking.alarms && !_.isEmpty(bedBooking.alarms[0])) {
        alarms = bedBooking.alarms[0];
      }
    }

    return alarms;
  };

  const renderBedBookingFields = (field, label, string) => {
    const getVal = (value) => {
      if (string) return value;
      else return value ? `Yes` : `No`;
    };

    if (bedBookingForm) {
      const form = _.cloneDeep(bedBookingForm);
      const note = form[`${field}Note`];
      const user = form[`${field}User`];

      let valueText = field === "requested" ? user : getVal(form[field]);

      if (field === "ward") {
        valueText = form[field]?.wardName || "";
      }

      return (
        <>
          <Grid item xs={12} md={6}>
            <FormLabel>{label}</FormLabel>

            <AgyleTextField
              fullWidth
              disabled
              // label={label}
              value={valueText}
              noBorder={false}
            />
          </Grid>

          {note && (
            <Grid item xs={12} md={6}>
              <FormLabel>{`${label} Note`}</FormLabel>

              <AgyleTextField
                fullWidth
                disabled
                // label={`${label} Note`}
                value={note}
                noBorder={false}
              />
            </Grid>
          )}
        </>
      );
    }
  };

  const getActivityTreatment = () => {
    let activityTreatment;
    if (!bedBookingForm.activityTreatment) {
      return;
    }
    activityTreatment = bedBookingForm.activityTreatment;

    return (
      <Grid item xs={12} md={6}>
        <FormLabel>Activity Treatment</FormLabel>

        <AgyleTextField
          fullWidth
          disabled
          noBorder={false}
          value={activityTreatment.__description}
        />
      </Grid>
    );
  };

  const getSdecSite = () => {
    let site;
    if (!bedBooking.sdecSite) {
      return;
    }
    site = bedBooking.sdecSite;

    return (
      <Grid item xs={12} md={6}>
        <FormLabel>Zone</FormLabel>

        <AgyleTextField
          fullWidth
          disabled
          noBorder={false}
          value={site.__description}
        />
      </Grid>
    );
  };

  const getCurrentSite = () => {
    let site;
    if (!bedBooking.currentSiteBedBooking) {
      return;
    }
    site = bedBooking.currentSiteBedBooking;

    return (
      <Grid item xs={12} md={6}>
        <FormLabel>Patient Location</FormLabel>

        <AgyleTextField
          fullWidth
          disabled
          noBorder={false}
          value={site.__description}
        />
      </Grid>
    );
  };

  const renderDemographicSection = () => {
    return demographicsSection.map((field) => {
      return field.type === "array" &&
        !_.isEmpty(field.value) &&
        field.value.length > 0
        ? field.value.map((singleArrayField, index) => {
            const key =
              field.key === "emailAddresses" ? "emailAddress" : "number";
            const textValue =
              key === "emailAddress"
                ? singleArrayField[key]
                : `${singleArrayField[key]} - ${singleArrayField["type"]}`;

            return (
              <Grid item xs={12} md={isSmall ? 12 : 4} key={field.key + index}>
                <FormLabel>{field.label}</FormLabel>

                <AgyleTextField
                  onChange={() => console.warn("Cannot change")}
                  fullWidth
                  disabled
                  noBorder={false}
                  value={textValue}
                />
              </Grid>
            );
          })
        : !_.isArray(field.value) && !!field.value && (
            <Grid item xs={12} md={isSmall ? 12 : 4} key={field.key}>
              <FormLabel>{field.label}</FormLabel>

              <AgyleTextField
                onChange={() => console.warn("Cannot change")}
                fullWidth
                disabled
                noBorder={false}
                value={field.value}
              />
            </Grid>
          );
    });
  };

  const isTemporaryAddressFilled = useMemo(() => {
    return (!!admit.streetTemporary || !!admit.postTownTemporary || !!admit.cityTemporary || !!admit.countyTemporary || !!admit.postalCodeTemporary);
  }, [admit?.streetTemporary, admit?.postTownTemporary, admit?.cityTemporary, admit?.countyTemporary, admit?.postalCodeTemporary]);

  const renderDemographicsAddressSection = (isTemporaryAddress = false) => {
    const arrayToMapThrough = isTemporaryAddress ? demographicsTemporaryAddressSection : demographicsAddressSection;

    return arrayToMapThrough.map((field) => {
      return field.type === "array" &&
        !_.isEmpty(field.value) &&
        field.value.length > 0
        ? field.value.map((singleArrayField, index) => {
            const key =
              field.key === "emailAddresses" ? "emailAddress" : "number";
            const textValue =
              key === "emailAddress"
                ? singleArrayField[key]
                : `${singleArrayField[key]} - ${singleArrayField["type"]}`;

            return (
              <Grid item xs={12} md={isSmall ? 12 : 4} key={field.key + index}>
                <FormLabel>{field.label}</FormLabel>

                <AgyleTextField
                  onChange={() => console.warn("Cannot change")}
                  fullWidth
                  disabled
                  noBorder={false}
                  value={textValue}
                />
              </Grid>
            );
          })
        : !_.isArray(field.value) && !!field.value && (
            <Grid item xs={12} md={isSmall ? 12 : 4} key={field.key}>
              <FormLabel>{field.label}</FormLabel>

              <AgyleTextField
                onChange={() => console.warn("Cannot change")}
                fullWidth
                disabled
                noBorder={false}
                value={field.value}
              />
            </Grid>
          );
    });
  };

  const renderNextOfKinSection = () => {
    return (
      !_.isEmpty(nextOfKinSection.value) &&
      nextOfKinSection.value.map((field, index) => {
        const { firstName, lastName, phoneNumber, relationship } = field;
        const fieldLabel = {
          firstName: "First Name",
          lastName: "Last Name",
          phoneNumber: "Phone Number",
          relationship: "Relationship",
        };

        const getFieldLabel = (key) => fieldLabel[key];
        const getFieldValue = (key) =>
          key === "relationship"
            ? _.capitalize(field[key]?.__description)
            : field[key]
              ? field[key]
              : "NR";

        return (
          <>
            <Grid item xs={12} sx={{pl: 2, mt: index === 0 ? 2 : 0}}>
              <Typography variant="h6">
                {t(`Next of Kin no. ${index + 1}`)}
              </Typography>
            </Grid>

            <Grid item xs={12} md={isSmall ? 12 : 3} key={field.key + index + "1"}>
              <FormLabel>{getFieldLabel('firstName')}</FormLabel>

              <AgyleTextField
                onChange={() => console.warn("Cannot change")}
                fullWidth
                disabled
                noBorder={false}
                value={getFieldValue("firstName")}
              />
            </Grid>

            <Grid item xs={12} md={isSmall ? 12 : 3} key={field.key + index + "2"}>
              <FormLabel>{getFieldLabel('lastName')}</FormLabel>

              <AgyleTextField
                onChange={() => console.warn("Cannot change")}
                fullWidth
                disabled
                noBorder={false}
                value={getFieldValue("lastName")}
              />
            </Grid>

            <Grid item xs={12} md={isSmall ? 12 : 3} key={field.key + index + "3"}>
              <FormLabel>{getFieldLabel('relationship')}</FormLabel>

              <AgyleTextField
                onChange={() => console.warn("Cannot change")}
                fullWidth
                disabled
                noBorder={false}
                value={getFieldValue("relationship")}
              />
            </Grid>

            <Grid item xs={12} md={isSmall ? 12 : 3} key={field.key + index + "4"}>
              <FormLabel>{getFieldLabel('phoneNumber')}</FormLabel>

              <AgyleTextField
                onChange={() => console.warn("Cannot change")}
                fullWidth
                disabled
                noBorder={false}
                value={getFieldValue("phoneNumber")}
              />
            </Grid>
          </>
        );
      })
    );
  };

  const renderNextOfKinAddressSection = () => {
    return (
      !_.isEmpty(nextOfKinSection.value) &&
      nextOfKinSection.value.map((field, index) => {
        const { city, county, postTown, postalCode, street } = field;
        const fieldLabel = {
          city: "City",
          county: "County",
          postTown: "Post Town",
          postalCode: "Postal Code",
          street: "Street",
        };

        const getFieldLabel = (key) => fieldLabel[key];
        const getFieldValue = (key) =>
          key === "relationship"
            ? _.capitalize(field[key]?.__description)
            : field[key]
              ? field[key]
              : "NR";

        return (
          <>
            <Grid item xs={12}>
              {t(`Address`)}
            </Grid>

            <Grid item xs={12} md={isSmall ? 12 : 4} key={field.key + index + "6"}>
              <FormLabel>{getFieldLabel('street')}</FormLabel>

              <AgyleTextField
                onChange={() => console.warn("Cannot change")}
                fullWidth
                disabled
                noBorder={false}
                value={getFieldValue("street")}
              />
            </Grid>

            <Grid item xs={12} md={isSmall ? 12 : 4} key={field.key + index + "7"}>
              <FormLabel>{getFieldLabel('city')}</FormLabel>

              <AgyleTextField
                onChange={() => console.warn("Cannot change")}
                fullWidth
                disabled
                noBorder={false}
                value={getFieldValue("city")}
              />
            </Grid>

            <Grid item xs={12} md={isSmall ? 12 : 4} key={field.key + index + "5"}>
              <FormLabel>{getFieldLabel('postalCode')}</FormLabel>

              <AgyleTextField
                onChange={() => console.warn("Cannot change")}
                fullWidth
                disabled
                noBorder={false}
                value={getFieldValue("postalCode")}
              />
            </Grid>

            <Grid item xs={12} md={isSmall ? 12 : 6} key={field.key + index + "9"}>
              <FormLabel>{getFieldLabel('county')}</FormLabel>

              <AgyleTextField
                onChange={() => console.warn("Cannot change")}
                fullWidth
                disabled
                noBorder={false}
                value={getFieldValue("county")}
              />
            </Grid>

            <Grid item xs={12} md={isSmall ? 12 : 6} key={field.key + index + "8"}>
              <FormLabel>{getFieldLabel('postTown')}</FormLabel>

              <AgyleTextField
                onChange={() => console.warn("Cannot change")}
                fullWidth
                disabled
                noBorder={false}
                value={getFieldValue("postTown")}
              />
            </Grid>
          </>
        );
      })
    );
  };

  const renderChildSection = () => {
    return childSection.map((field, index) => {
      const fieldValue =
        field.type === "boolean" ? (field.value ? "Yes" : "No") : field.value;

      return (
        !!field.value && (
          <Grid item xs={12} md={isSmall ? 12 : 6} key={field.key + index}>
            <FormLabel>{field.label}</FormLabel>

            <AgyleTextField
              onChange={() => console.warn("Cannot change")}
              fullWidth
              disabled
              value={fieldValue}
            />
          </Grid>
        )
      );
    });
  };

  const renderAttendanceSection = () => {
    return attendanceSection.map((field, index) => {
      const fieldValue =
        field.type === "boolean"
          ? field.value
            ? "Yes"
            : "No"
          : field.type === "date"
            ? moment(field.value).format(config?.defaultDateTimeFormat)
            : field.value;

      return (
        !!field.value && (
          <Grid item xs={12} md={isSmall ? 12 : 4} key={field.key + index}>
            <FormLabel>{field.label}</FormLabel>

            <AgyleTextField
              onChange={() => console.warn("Cannot change")}
              fullWidth
              disabled
              noBorder={false}
              value={`${fieldValue}`}
            />
          </Grid>
        )
      );
    });
  };

  return (
    <Grid container gap={1} sx={{ pt: 2 }}>
      <Grid item xs={12}>
        <AgyleAccordion
          label={t("Summary")}
          expanded={summaryAccordionExpanded}
          onChange={() =>
            setSummaryAccordionExpanded(!summaryAccordionExpanded)
          }
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sx={{ my: 2, pl: 2 }}>
              <Typography variant="h6">Patient Demographics</Typography>
            </Grid>

            {renderDemographicSection()}

            <Grid item xs={12} sx={{pl: 2, mt: 2}}>
              <Typography variant="h6">Address</Typography>
            </Grid>

            {renderDemographicsAddressSection(false)}

            {isTemporaryAddressFilled && (
              <>
                <Grid item xs={12} sx={{pl: 2, mt: 2}}>
                  <Typography variant="h6">Temporary Address</Typography>
                </Grid>

                {renderDemographicsAddressSection(true)}
              </>
            )}

            {renderNextOfKinSection()}

            {renderNextOfKinAddressSection()}

            {/*CHILD DETAILS SECTION*/}
            {admit.dob &&
              isChild(patientAge) &&
              (admission.cpIs ||
                admission.cpIsAlertPresent ||
                admit.schoolName ||
                admit.schoolAddress ||
                admit.schoolPostalCode ||
                admit.schoolAddress) && (
                <>
                  <Grid item xs={12} sx={{pl: 2, mt: 2}}>
                    <Typography variant="h6">Details</Typography>
                  </Grid>

                  {renderChildSection()}
                </>
              )}

            {/*  ADMISSION SECTION  */}
            {!isAddOnlyPatient && (
              <Grid item xs={12} sx={{mt: 2, ml: 0, pl: 2}}>
                <Typography variant="h6">
                  {t(
                    isBedBookingChosen
                      ? "Bed Booking Details"
                      : "Attendance Details",
                  )}
                </Typography>
              </Grid>
            )}

            {isBedBookingChosen ? (
              <>
                {!_.isEmpty(getBedBookingAlarms()) &&
                  getBedBookingAlarms().map((alarm, index) => {
                    return (
                      <Grid item xs={12} md={isSmall ? 12 : 6} key={alarm.name + index}>
                        <FormLabel>{alarm.name}</FormLabel>

                        <AgyleTextField
                          fullWidth
                          disabled
                          noBorder={false}
                          value={_.startCase(alarm.state)}
                        />
                      </Grid>
                    );
                  })}

                {renderBedBookingFields("requested", "Requested By")}
                {renderBedBookingFields("sideRoom", "Side Room")}
                {renderBedBookingFields(
                  "patientPost",
                  "Patient Post Take Complete",
                )}
                {renderBedBookingFields(
                  "anotherDestination",
                  "Site bed is requested at",
                  true,
                )}
                {renderBedBookingFields("allocated", "Allocated")}
                {renderBedBookingFields("ward", "Ward")}

                {getCurrentSite()}
                {getSdecSite()}
                {getActivityTreatment()}
              </>
            ) : (
              renderAttendanceSection()
            )}
          </Grid>
        </AgyleAccordion>
      </Grid>
    </Grid>
  );
};

export default Summary;
